import { FunctionComponent, SyntheticEvent, useEffect, useState } from 'react'
import { RegularSlot } from '@/types/Slots'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import { useTranslation } from 'react-i18next'
import styles from './confirmDeleteRegularSlotDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { CalendarMonth, Close, Delete } from '@mui/icons-material'
import { useLazyQuery, useMutation } from '@apollo/client'
import {
    DELETE_MULTIPLE_REGULAR_SLOTS,
    DELETE_REGULAR_SLOT,
    GET_REGULAR_SLOTS
} from '@/modules/API/slot/regularSlotHandler'
import moment, { Moment } from 'moment/moment'
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    Chip,
    TextField
} from '@mui/material'
import { format } from 'date-fns'
import { getLocalFromLanguage } from '@/modules/helper/languageHelper.ts'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import SelectMultipleDateDialogComponent from '../../occasionalSlot/occasionalSlotForm/components/SelectMultipleDateDialog/selectMultipleDateDialog'
import { Daycare } from '@/types/Daycare.ts'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import { CalendarEventType } from '@/types/Calendar.ts'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import { getDaycareDaysSelectOptions } from '@/modules/helper/daycareHelper.ts'
import { ClosingDay } from '@/types/ClosingDays.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useDialog } from '@/hooks/useDialog/useDialog'
import { DAY_FORMAT } from '../../occasionalSlot/models/constants'

interface ConfirmDeleteRegularSlotDialogProps {
    open: boolean
    handleClose: () => void
    regularSlots: RegularSlot[]
    date: Date | null
    daycare: Daycare
    closeParent: () => void
    lastRegularSlotDate: string
    refetchRegularSlots: () => void
    closingDays: ClosingDay[]
}

type slotIdAndDateAndChildId = {
    slotId: number
    date: string
    childId: number
}

const ConfirmDeleteRegularSlotDialog: FunctionComponent<ConfirmDeleteRegularSlotDialogProps> = ({
    open,
    handleClose,
    regularSlots,
    date,
    daycare,
    closeParent,
    lastRegularSlotDate,
    refetchRegularSlots,
    closingDays
}: ConfirmDeleteRegularSlotDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [chooseDate, setChooseDate] = useState<boolean>(false)
    const [dateList, setDateList] = useState<string[]>([])

    const [selectPeriod, setSelectPeriod] = useState<boolean>(false)
    const [startDate, setStartDate] = useState<string | null>(null)
    const [endDate, setEndDate] = useState<string | null>(null)

    const [chooseDays, setChooseDays] = useState<boolean>(false)
    const [selectedDays, setSelectedDays] = useState<string[]>([])

    const [selectChilds, setSelectChilds] = useState<string[]>(
        regularSlots.map((slot: RegularSlot): string => slot.childId.toString())
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: regularSlots.map((slot: RegularSlot): SelectOptionDeprecated => {
                return {
                    name: slot.childName + (slot.childComplement ? ' - ' + slot.childComplement : ''),
                    value: slot.childId.toString()
                }
            })
        }
    ]

    function handleCheckChooseDate(checked: boolean): void {
        if (dateList.length === 0) setDateList([moment(date).format('DD-MM-YYYY')])
        if (checked) openSelectMultipleDateDialog()
        setChooseDate(checked)

        if (selectPeriod) {
            setSelectPeriod(false)
            setStartDate(null)
            setEndDate(null)
        }
    }

    function handleDateRemove(dateToRemove: string): void {
        setDateList(dateList.filter((date: string): boolean => date !== dateToRemove))
    }

    function handleCheckSelectPeriod(checked: boolean): void {
        if (startDate === null || endDate === null) {
            setStartDate(moment(date).format('DD-MM-YYYY'))
            setEndDate(moment(date).format('DD-MM-YYYY'))
        }

        setSelectPeriod(checked)

        if (chooseDate) {
            setChooseDate(false)
            setDateList([])
        }
    }

    const selectDays: SelectOptionsDeprecated = getDaycareDaysSelectOptions(daycare, t)

    const [deleteRegularSlot] = useMutation(DELETE_REGULAR_SLOT, {
        onCompleted: () => {
            showNotificationSuccess(t('regularSlotDeleted'))

            refetchRegularSlots()

            handleClose()
            closeParent()
        },
        onError: () => {
            showNotificationError(t('errorDeletedRegularSlot'))
        }
    })

    const [deleteMultipleRegularSlot] = useMutation(DELETE_MULTIPLE_REGULAR_SLOTS, {
        onCompleted: () => {
            showNotificationSuccess(t('regularSlotDeleted'))

            refetchRegularSlots()

            handleClose()
            closeParent()
        },
        onError: () => {
            showNotificationError(t('errorDeletedRegularSlot'))
        }
    })

    function deleteAllRegularSlots(): void {
        if ((chooseDate && dateList.length > 0) || selectPeriod) {
            const regularSlotToDelete: slotIdAndDateAndChildId[] = []
            const dateListToDelete: string[] =
                selectPeriod && startDate && endDate
                    ? getDatesBetweenTwoDates(startDate, endDate, selectedDays, closingDays)
                    : dateList

            dateListToDelete.forEach((date: string) => {
                regularSlotsInCalendar
                    .filter((slot: slotIdAndDateAndChildId): boolean => selectChilds.includes(slot.childId.toString()))
                    .filter((slot: slotIdAndDateAndChildId): boolean => slot.date === date)
                    .forEach((slot: slotIdAndDateAndChildId) => {
                        regularSlotToDelete.push(slot)
                    })
            })

            deleteMultipleRegularSlot({
                variables: {
                    daycareId: daycare.id,
                    regularSlotIds: regularSlotToDelete.map((slot: slotIdAndDateAndChildId): number => slot.slotId)
                }
            })
        } else {
            regularSlots
                .filter((slot: RegularSlot): boolean => selectChilds.includes(slot.childId.toString()))
                .forEach((slot: RegularSlot): void => {
                    deleteRegularSlot({
                        variables: {
                            regular_slot_id: slot.id,
                            daycare_id: slot.daycareId
                        }
                    })
                })
        }
    }

    const [otherDisabledDates, setOtherDisabledDates] = useState<Date[]>([])

    const [regularSlotsInCalendar, setRegularSlotsInCalendar] = useState<slotIdAndDateAndChildId[]>([])

    const [getRegularSlots, { data: dataRegularSlots, loading: loadingRegularSlots }] = useLazyQuery(GET_REGULAR_SLOTS)

    useEffect(() => {
        getRegularSlots({
            variables: {
                from: moment().format('DD-MM-YYYY'),
                to: lastRegularSlotDate,
                daycareId: daycare.id,
                childIds: selectChilds.map((childId: string): number => Number(childId))
            }
        })
    }, [selectChilds])

    useEffect(() => {
        const dateToShow: string[] = []
        const regularSlotsCalendar: slotIdAndDateAndChildId[] = []

        dataRegularSlots?.getRegularSlots.map((regularSlot: RegularSlot): void => {
            if (regularSlot.type === CalendarEventType.REGULAR) {
                dateToShow.push(regularSlot.date)

                regularSlotsCalendar.push({
                    slotId: regularSlot.id,
                    date: regularSlot.date,
                    childId: regularSlot.childId
                })
            }
        })

        setRegularSlotsInCalendar(regularSlotsCalendar)

        const start: Moment = moment().startOf('month')
        const end: Moment = start.clone().add(1, 'year').endOf('month')

        const result: Date[] = []

        let currentDate: Moment = start.clone()
        while (currentDate.isSameOrBefore(end)) {
            const formatted: string = currentDate.format('DD-MM-YYYY')
            if (!dateToShow.includes(formatted)) {
                result.push(currentDate.toDate())
            }
            currentDate = currentDate.add(1, 'days')
        }

        setOtherDisabledDates(result)
    }, [dataRegularSlots])

    const { dialog: SelectMultipleDateDialog, openDialog: openSelectMultipleDateDialog } = useDialog({
        Component: SelectMultipleDateDialogComponent,
        props: () => ({
            daycare,
            dateList,
            setDateList,
            startDate: moment(date).format(DAY_FORMAT),
            closingDays: [],
            otherDisabledDates
        }),
        dialogTitle: false
    })

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('deleteRegularSlot')} />
            <DialogBody className={styles.DialogBody}>
                <div className={styles.Container}>
                    {chooseDate && dateList.length > 0 ? (
                        <Autocomplete
                            multiple
                            options={dateList}
                            value={dateList}
                            onChange={(_event: SyntheticEvent, newValue: string[]) => setDateList(newValue)}
                            renderTags={(value: string[], getTagProps: AutocompleteRenderGetTagProps) =>
                                value.map((option: string, index: number) => {
                                    const { key, ...tagProps } = getTagProps({ index })

                                    return (
                                        <Chip
                                            key={key}
                                            label={option}
                                            {...tagProps}
                                            onDelete={() => handleDateRemove(option)}
                                        />
                                    )
                                })
                            }
                            renderInput={(params: AutocompleteRenderInputParams) => (
                                <TextField
                                    {...params}
                                    variant='outlined'
                                    rows={1}
                                    multiline={true}
                                    label={t('dateList')}
                                />
                            )}
                            className={styles.AutocompleteDaycare}
                            onOpen={openSelectMultipleDateDialog}
                        />
                    ) : selectPeriod && startDate && endDate ? (
                        <div className={styles.InputContainer}>
                            <div className={styles.Input}>
                                <DatePickerComponent
                                    className={styles.UserInput}
                                    value={startDate.valueOf()}
                                    setValue={setStartDate}
                                    maxDate={endDate.valueOf()}
                                    disablePast={true}
                                    label={t('fromDate')}
                                />
                                <DatePickerComponent
                                    className={styles.UserInput}
                                    value={endDate.valueOf()}
                                    setValue={setEndDate}
                                    minDate={startDate.valueOf()}
                                    label={t('toDate')}
                                />
                            </div>
                            <div className={styles.VerticalInput}>
                                <div className={styles.CheckboxInput}>
                                    <Checkbox
                                        text={t('chooseRecurrence')}
                                        checked={chooseDays}
                                        onChange={(checked: boolean) => setChooseDays(checked)}
                                        className={styles.Checkbox}
                                    />
                                </div>
                                {chooseDays && (
                                    <SelectMultiple
                                        selectOptions={selectDays}
                                        label={t('days')}
                                        selectValues={selectedDays}
                                        setSelectValues={setSelectedDays}
                                        className={styles.UserInput}
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        <>
                            <CalendarMonth className={`${styles.Icon} ${styles.IconDaycare}`} />
                            <div className={styles.TextContainer}>
                                {date && (
                                    <p className={styles.FirstText}>
                                        {format(date, 'EEEE dd MMMM yyyy', {
                                            locale: getLocalFromLanguage()
                                        })}
                                    </p>
                                )}
                                <p className={styles.SecondText}>
                                    {t('from') +
                                        ' ' +
                                        moment(regularSlots[0].start, 'HH:mm:ss').format('HH:mm') +
                                        ' ' +
                                        t('to').toLowerCase() +
                                        ' ' +
                                        moment(regularSlots[0].end, 'HH:mm:ss').format('HH:mm')}
                                </p>
                            </div>
                        </>
                    )}
                </div>
                <div className={styles.CheckboxContainer}>
                    <Checkbox
                        text={t('selectOtherDates')}
                        checked={chooseDate}
                        onChange={(checked: boolean) => handleCheckChooseDate(checked)}
                        className={styles.Checkbox}
                    />
                    <Checkbox
                        text={t('selectPeriod')}
                        checked={selectPeriod}
                        onChange={(checked: boolean) => handleCheckSelectPeriod(checked)}
                        className={styles.Checkbox}
                    />
                </div>
                <div className={styles.BreakLine}></div>
                {regularSlots.length > 1 && (
                    <SelectMultiple
                        selectOptions={selectOptionsChild}
                        selectValues={selectChilds}
                        setSelectValues={setSelectChilds}
                        className={styles.UserInput}
                        label={t('childs')}
                    />
                )}
                <div className={styles.DialogContainer}>
                    <p className={styles.Text}>{t('deleteRegularSlotMessage')}</p>
                    <p className={styles.Text}>{t('noRollBack')}</p>
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Delete />}
                        customText={t('yes')}
                        onClick={deleteAllRegularSlots}
                        className={styles.Button}
                        disabled={loadingRegularSlots}
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customIcon={<Close />}
                        customText={t('no')}
                        onClick={handleClose}
                        className={styles.Button}
                        disabled={loadingRegularSlots}
                    />
                </div>
            </DialogBody>
            {SelectMultipleDateDialog}
        </Dialog>
    )
}

export default ConfirmDeleteRegularSlotDialog

export function getDatesBetweenTwoDates(
    startDate: string,
    endDate: string,
    selectedDays: string[],
    closingDays: ClosingDay[]
): string[] {
    const daysOfClosing: string[] = closingDays
        .map((closingDay: ClosingDay): string[] => {
            const startClosing: Moment = moment(closingDay.startDate, 'DD-MM-YYYY')
            const endClosing: Moment = moment(closingDay.endDate, 'DD-MM-YYYY')
            const days: string[] = []

            while (startClosing.isBefore(endClosing, 'day')) {
                days.push(startClosing.format('DD-MM-YYYY'))
                startClosing.add(1, 'day')
            }

            days.push(endClosing.format('DD-MM-YYYY'))

            return days
        })
        .flat()

    const start: Moment = moment(startDate, 'DD-MM-YYYY')
    const end: Moment = moment(endDate, 'DD-MM-YYYY')
    const days: string[] = []

    while (start.isBefore(end, 'day')) {
        if (
            !daysOfClosing.includes(start.format('DD-MM-YYYY')) &&
            (selectedDays.length === 0 || selectedDays.includes(((start.day() + 6) % 7).toString()))
        ) {
            days.push(start.format('DD-MM-YYYY'))
        }
        start.add(1, 'day')
    }

    if (
        !daysOfClosing.includes(start.format('DD-MM-YYYY')) &&
        (selectedDays.length === 0 || selectedDays.includes(((end.day() + 6) % 7).toString()))
    ) {
        days.push(end.format('DD-MM-YYYY'))
    }

    return days
}
